/**
 * Category Translation Utility
 * Handles display of translated category names and subcategories
 */

export interface TranslatedCategory {
  id: string;
  name: string;
  translatedName?: Record<string, string>;
  subcategories?: string[];
  translatedSubCategories?: Record<string, string[] | Record<string, string>>; // Can be array or object
  description?: string;
  type?: any;
  isEmergency?: boolean;
  isRequireVerification?: boolean;
  requirements?: string[];
}

/**
 * Get translated category name
 * Falls back to English name if translation not available
 * 
 * @param category - Category object with translations
 * @param languageCode - Language code (it, es, fr, etc.)
 * @returns Translated name or fallback to English
 */
export const getTranslatedCategoryName = (
  category: TranslatedCategory | null | undefined,
  languageCode: string = "en"
): string => {
  if (!category) return "";
  
  // If English or no language code, return original name
  if (!languageCode || languageCode === "en") {
    return category.name || "";
  }
  
  // Try to get translated name
  const translatedName = category.translatedName?.[languageCode];
  
  // Return translated name if available, otherwise fallback to English
  return translatedName || category.name || "";
};

/**
 * Get translated subcategories
 * Falls back to English subcategories if translation not available
 * 
 * @param category - Category object with translations
 * @param languageCode - Language code (it, es, fr, etc.)
 * @returns Array of translated subcategories or fallback to English
 */
export const getTranslatedSubcategories = (
  category: TranslatedCategory | null | undefined,
  languageCode: string = "en"
): string[] => {
  if (!category) return [];
  
  // If English or no language code, return original subcategories
  if (!languageCode || languageCode === "en") {
    return category.subcategories || [];
  }
  
  // Try to get translated subcategories
  const translatedSubcats = category.translatedSubCategories?.[languageCode];
  
  if (!translatedSubcats) {
    return category.subcategories || [];
  }
  
  // Handle both formats: array and object
  if (Array.isArray(translatedSubcats)) {
    // Array format: ['Installazione', 'Riparazione', ...]
    return translatedSubcats;
  } else if (typeof translatedSubcats === "object") {
    // Object format: {Installation: 'Installazione', Repair: 'Riparazione', ...}
    // Map original subcategories to their translations
    const originalSubcats = category.subcategories || [];
    return originalSubcats.map(original => translatedSubcats[original] || original);
  }
  
  // Fallback to original subcategories
  return category.subcategories || [];
};

/**
 * Get translated subcategory by index
 * 
 * @param category - Category object with translations
 * @param subcategoryValue - Subcategory value (English)
 * @param languageCode - Language code (it, es, fr, etc.)
 * @returns Translated subcategory or original value
 */
export const getTranslatedSubcategory = (
  category: TranslatedCategory | null | undefined,
  subcategoryValue: string,
  languageCode: string = "en"
): string => {
  if (!category || !subcategoryValue) return subcategoryValue || "";
  
  // If English or no language code, return original value
  if (!languageCode || languageCode === "en") {
    return subcategoryValue;
  }
  
  // Find index of subcategory in original array
  const originalSubcategories = category.subcategories || [];
  const index = originalSubcategories.indexOf(subcategoryValue);
  
  if (index === -1) {
    return subcategoryValue; // Not found, return original
  }
  
  // Get translated subcategories array
  const translatedSubcats = category.translatedSubCategories?.[languageCode];
  
  if (!translatedSubcats || !translatedSubcats[index]) {
    return subcategoryValue; // Translation not available, return original
  }
  
  return translatedSubcats[index];
};

/**
 * Create a translated category object with all fields translated
 * This is useful for components that need the entire category object with translations
 * 
 * @param category - Original category object
 * @param languageCode - Language code (it, es, fr, etc.)
 * @returns Category object with name and subcategories translated
 */
export const getFullyTranslatedCategory = (
  category: TranslatedCategory,
  languageCode: string = "en"
): TranslatedCategory => {
  return {
    ...category,
    name: getTranslatedCategoryName(category, languageCode),
    subcategories: getTranslatedSubcategories(category, languageCode),
  };
};

/**
 * Translate an array of categories
 * 
 * @param categories - Array of categories
 * @param languageCode - Language code (it, es, fr, etc.)
 * @returns Array of categories with translated names and subcategories
 */
export const translateCategories = (
  categories: TranslatedCategory[],
  languageCode: string = "en"
): TranslatedCategory[] => {
  if (!categories || !Array.isArray(categories)) return [];
  
  return categories.map(category => getFullyTranslatedCategory(category, languageCode));
};

/**
 * Check if category has translations for a specific language
 * 
 * @param category - Category object
 * @param languageCode - Language code to check
 * @returns Boolean indicating if translations exist
 */
export const hasTranslation = (
  category: TranslatedCategory | null | undefined,
  languageCode: string
): boolean => {
  if (!category || !languageCode || languageCode === "en") return true;
  
  const hasNameTranslation = !!category.translatedName?.[languageCode];
  const hasSubcatTranslation = !!category.translatedSubCategories?.[languageCode];
  
  return hasNameTranslation || hasSubcatTranslation;
};

/**
 * Get available translations for a category
 * 
 * @param category - Category object
 * @returns Array of language codes that have translations
 */
export const getAvailableTranslations = (
  category: TranslatedCategory | null | undefined
): string[] => {
  if (!category) return ["en"];
  
  const languages = new Set<string>(["en"]); // English is always available
  
  // Add languages from translatedName
  if (category.translatedName) {
    Object.keys(category.translatedName).forEach(lang => languages.add(lang));
  }
  
  // Add languages from translatedSubCategories
  if (category.translatedSubCategories) {
    Object.keys(category.translatedSubCategories).forEach(lang => languages.add(lang));
  }
  
  return Array.from(languages);
};

